/**
 * ContentLabelTag - A Tailwind/shadcn implementation
 *
 * Displays a label tag with an emoji/icon section and text.
 * Matches the sked-ui MultiContentLabelSelector styling:
 * - Light gray pill background
 * - Colored square on left with emoji (contrast-aware text)
 * - Dark text for label name
 */
import React from "react";
import { cn } from "@/lib/utils";
import { getContrastTextColor } from "utils/color";
import { toNative } from "libs/emoji";

export interface ContentLabelTagProps {
  /** Unique identifier for the tag */
  id: string;
  /** Display text for the label */
  text: string;
  /** Size variant */
  size?: "sm" | "md";
  /** Background color for the emoji section (hex color) */
  color?: string;
  /** Emoji unicode (e.g., "1f389" for 🎉) */
  emoji?: string;
  /** Whether this is a campaign label (shows purple background with asterisk icon) */
  isCampaign?: boolean;
  /** Called when delete button is clicked */
  onDelete?: (id: string) => void;
  /** Additional CSS classes */
  className?: string;
  /** Optional width clamp used by legacy Insights tables */
  textMaxWidth?: string;
  /** Optional font family override used in exported Insights views */
  fontFamily?: string;
  /** Optional font weight override used in exported Insights views */
  fontWeight?: number | string;
  /** Legacy layout hint retained for existing styled wrappers */
  hasSpecialWidth?: boolean;
}

// Default colors
const DEFAULT_COLOR = "#9ca3af"; // neutral-400 as fallback
const CAMPAIGN_COLOR = "#edeaff";
const CAMPAIGN_ICON_COLOR = "#0e1680";

/**
 * A label tag component that displays content pillar or campaign labels.
 *
 * The entire tag uses the user's selected color as background,
 * with contrast-aware text color (black or white) for readability.
 * Campaigns use a purple background with asterisk icon.
 */
export function ContentLabelTag({
  id,
  text,
  size = "md",
  color,
  emoji,
  isCampaign = false,
  onDelete,
  className,
  textMaxWidth,
  fontFamily,
  fontWeight,
}: ContentLabelTagProps) {
  // Size-based classes
  const sizeConfig = {
    sm: {
      container: "h-5 text-xs px-1.5 gap-1",
      iconSize: "text-[10px]",
      emojiSize: "text-xs",
      deleteButton: "w-4 h-4 -mr-0.5",
      deleteIcon: "text-[8px]",
    },
    md: {
      container: "h-6 text-sm px-2 gap-1.5",
      iconSize: "text-xs",
      emojiSize: "text-sm",
      deleteButton: "w-5 h-5 -mr-0.5",
      deleteIcon: "text-[10px]",
    },
  };

  const config = sizeConfig[size];

  // Determine tag background color - full tag is colored
  const tagBackgroundColor = isCampaign
    ? CAMPAIGN_COLOR
    : color || DEFAULT_COLOR;

  // Calculate contrast-aware text color based on background
  const textColor = isCampaign
    ? CAMPAIGN_ICON_COLOR
    : getContrastTextColor(tagBackgroundColor);

  // Convert emoji unicode to native if provided
  const displayEmoji = emoji ? toNative(emoji) : null;

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete?.(id);
  };

  return (
    <div
      data-testid={`content-label-tag-${id}`}
      className={cn(
        "inline-flex items-center rounded-full",
        config.container,
        className
      )}
      style={{ backgroundColor: tagBackgroundColor, fontFamily, fontWeight }}
    >
      {/* Emoji/icon */}
      {isCampaign ? (
        <i
          className={cn("fa-solid fa-asterisk shrink-0", config.iconSize)}
          style={{ color: textColor }}
          aria-hidden="true"
        />
      ) : displayEmoji ? (
        <span className={cn("shrink-0", config.emojiSize)} aria-hidden="true">
          {displayEmoji}
        </span>
      ) : (
        <span className={cn("shrink-0", config.emojiSize)} aria-hidden="true">
          🎉
        </span>
      )}

      {/* Text section */}
      <span
        className="font-normal truncate max-w-[200px]"
        style={{ color: textColor, maxWidth: textMaxWidth }}
      >
        {text}
      </span>

      {/* Delete button (optional) */}
      {onDelete && (
        <button
          type="button"
          onClick={handleDelete}
          className={cn(
            "flex items-center justify-center shrink-0",
            "opacity-70 hover:opacity-100",
            "transition-opacity",
            config.deleteButton
          )}
          style={{ color: textColor }}
          aria-label={`Remove ${text} label`}
        >
          <i
            className={cn("fa-regular fa-xmark", config.deleteIcon)}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
}

export default ContentLabelTag;
